import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../Button";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
    EditEmissionsSourceFormValues,
    editEmissionsSourceValidationFields,
} from "./EditEmissionsSourceFormValues";
import {
    useEmissionsSourceItems,
    usePortfolios,
    useSectors,
    useUniqueReferenceIsUnique,
} from "../../api/emissions-sources";
import FormTextInput from "../FormTextInput";
import FormDropdown from "../FormDropdown";
import { useLocations } from "../../api/location";
import EmissionsSourceItemDetail from "../EmissionsSourceItemDetailForm";

type EditEmissionsSourceProps = {
    onSubmit: (values: EditEmissionsSourceFormValues) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    initialValues: Partial<EditEmissionsSourceFormValues>;
    id: number;
};

const EditEmissionsSourceForm = ({
    onSubmit,
    onCancel,
    isSubmitting,
    initialValues,
    id,
}: EditEmissionsSourceProps) => {
    const uniqueReferenceIsUnique = useUniqueReferenceIsUnique();

    const validationSchema: yup.SchemaOf<EditEmissionsSourceFormValues> =
        yup.object(
            editEmissionsSourceValidationFields(uniqueReferenceIsUnique, id)
        );

    const methods = useForm<EditEmissionsSourceFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(validationSchema),
        defaultValues: { ...initialValues },
    });

    const { locations, isLoading: isLoadingLocations } = useLocations();
    const { sectors, isLoading: isLoadingSectors } = useSectors();
    const { portfolios, isLoading: isLoadingPortfolios } = usePortfolios();

    const locationOptions = locations
        ? locations.map((location) => ({
              value: location.id,
              label: location.name,
          }))
        : [];

    const sectorOptions = sectors
        ? sectors.map((sector) => ({
              value: sector.id,
              label: sector.name,
          }))
        : [];

    const portfolioOptions = portfolios
        ? portfolios.map((portfolio) => ({
              value: portfolio.id,
              label: portfolio.name,
          }))
        : [];

    const { emissionsSourceItems, isLoading: isLoadingEmissionsSourceItems } =
        useEmissionsSourceItems();

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className="space-y-4 max-w-xl rounded-xl bg-surface-light-1 p-6 mx-auto"
            >
                <FormTextInput
                    required
                    label="Name"
                    placeholder="Enter asset name"
                    {...methods.register("name")}
                    error={methods.formState.errors.name}
                />
                <FormTextInput
                    required
                    label="Unique reference"
                    placeholder="Enter unique reference"
                    {...methods.register("uniqueReference")}
                    error={methods.formState.errors.uniqueReference}
                />
                <Controller
                    name="locationId"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormDropdown
                            required
                            isLoadingOptions={isLoadingLocations}
                            label="Location"
                            pleaseSelectText="Select location"
                            options={locationOptions}
                            value={value}
                            onChange={onChange}
                            error={methods.formState.errors.locationId}
                        />
                    )}
                />
                <EmissionsSourceItemDetail
                    emissionsSourceItems={emissionsSourceItems}
                    isLoadingEmissionsSourceItems={
                        isLoadingEmissionsSourceItems
                    }
                />
                <Controller
                    name="portfolioId"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormDropdown
                            required
                            isLoadingOptions={isLoadingPortfolios}
                            label="Portfolio"
                            pleaseSelectText="Select location"
                            options={portfolioOptions}
                            value={value}
                            onChange={onChange}
                            error={methods.formState.errors.portfolioId}
                        />
                    )}
                />
                <Controller
                    name="sectorId"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormDropdown
                            required
                            isLoadingOptions={isLoadingSectors}
                            label="Sector"
                            pleaseSelectText="Select sector"
                            options={sectorOptions}
                            value={value}
                            onChange={onChange}
                            error={methods.formState.errors.sectorId}
                        />
                    )}
                />
                <FormTextInput
                    required
                    label="Enterprise Value"
                    placeholder="Enter enterprise value"
                    {...methods.register("enterpriseValue")}
                    type="number"
                    error={methods.formState.errors.enterpriseValue}
                />
                <FormTextInput
                    required
                    label="Outstanding Amount"
                    placeholder="Enter outstanding amount"
                    {...methods.register("outstandingAmount")}
                    type="number"
                    error={methods.formState.errors.outstandingAmount}
                />
                <div className="flex justify-end gap-6 pt-4">
                    <Button
                        variant="transparent"
                        type="button"
                        disabled={isSubmitting}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" loading={isSubmitting}>
                        Save
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default EditEmissionsSourceForm;
